import { faFileLines, faImage, faListUl } from '@fortawesome/free-solid-svg-icons';
import { toSavedListItems, validateListItems } from '../ListItemForm';
import { ContentEditor, ContentViewer } from './ContentType';
import { ListEditor, ListViewer } from './ListType';
import { ImageEditor, ImageViewer } from './ImageType';

// Every console item type, in the order they're offered. Each owns one top-level field on the item
// (content / listItems / image), which is exactly what customer sites read from the public API.
// Mirrors server/utils/consoleItemTypes.js: a new type needs an entry in both plus a model field.
//
// Editors get { draft, onChange, consoleId }; onChange takes a patch or (prevDraft) => patch.
// Viewers get { item, onAddListItem, onEditListItem, onDeleteListItem }.
export const ITEM_TYPES = {
  content: {
    label: 'Content',
    description: 'Formatted text for a page section',
    icon: faFileLines,
    badgeClass: 'bg-blue-500/20 text-blue-200',
    emptyDraft: () => ({ content: '' }),
    fromItem: (item) => ({ content: item.content || '' }),
    validate: (draft) => (draft.content?.trim() ? null : 'Please enter some content.'),
    toPayload: (draft) => ({ content: draft.content }),
    searchText: (item) => item.content || '',
    Editor: ContentEditor,
    Viewer: ContentViewer,
  },
  list: {
    label: 'List',
    description: 'Repeating entries, each with a title, description and an image or link',
    icon: faListUl,
    badgeClass: 'bg-purple-500/20 text-purple-200',
    emptyDraft: () => ({ listItems: [] }),
    fromItem: (item) => ({ listItems: item.listItems || [] }),
    validate: (draft, { isNew }) => {
      const listItems = draft.listItems || [];
      if (isNew && listItems.length === 0) return 'Please add at least one list item.';
      return validateListItems(listItems);
    },
    toPayload: (draft) => ({ listItems: toSavedListItems(draft.listItems || []) }),
    searchText: (item) => (item.listItems || []).map((entry) => `${entry.title} ${entry.description}`).join(' '),
    count: (item) => item.listItems?.length || 0,
    Editor: ListEditor,
    Viewer: ListViewer,
  },
  image: {
    label: 'Image',
    description: 'A single image with alt text, like a hero or profile photo',
    icon: faImage,
    badgeClass: 'bg-emerald-500/20 text-emerald-200',
    emptyDraft: () => ({ image: null }),
    fromItem: (item) => ({ image: item.image || null }),
    validate: (draft) => (draft.image?.url ? null : 'Please upload or choose an image.'),
    toPayload: (draft) => ({ image: draft.image }),
    searchText: (item) => item.image?.alt || '',
    Editor: ImageEditor,
    Viewer: ImageViewer,
  },
};

export const ITEM_TYPE_NAMES = Object.keys(ITEM_TYPES);

// Unknown types (e.g. added on the server first) fall back to content so the page still renders
export const getItemType = (name) => ITEM_TYPES[name] || ITEM_TYPES.content;
